/**
 * §P.1 · Revocar el acceso de un tutor.
 *
 * `DELETE` quita **el vínculo**, no la cuenta ni la membresía: el mismo adulto
 * puede ser tutor de un hermano, y esa otra relación no se toca. Y **no manda
 * ningún aviso** — si el club quiere que la persona se entere, eso es un mensaje,
 * no un efecto secundario de un botón.
 *
 * Las dos cosas hay que decirlas **antes** de pulsar, no después: quien revoca a
 * una madre necesita saber que no le va a llegar un correo diciéndoselo, y quien
 * revoca a un abuelo necesita saber que no le está borrando la cuenta.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi } from '@/shared/api/errores';
import { Error as PanelError } from '@/shared/ui/Estados';
import { nombreDeTutor, type Tutor } from '@/club/alumnos/tipos';

export function RevocarTutor({
  club,
  alumnoId,
  nombreDelAlumno,
  tutor,
  alTerminar,
  alCancelar,
}: {
  club: string;
  alumnoId: string;
  nombreDelAlumno: string;
  tutor: Tutor;
  alTerminar: () => void;
  alCancelar: () => void;
}) {
  const cache = useQueryClient();

  const revocar = useMutation({
    mutationFn: async () => {
      const r = await api.v1
        .clubs({ club })
        .students({ studentId: alumnoId })
        .guardians({ guardianId: tutor.user_id })
        .delete();
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
    },
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: ['tutores', club, alumnoId] });
      alTerminar();
    },
  });

  return (
    <div className="hueco hueco-confirmar">
      <h2>¿Quitarle el acceso a {nombreDeTutor(tutor)}?</h2>

      <p>
        Dejará de ver a {nombreDelAlumno} en su app: el progreso, los avisos y
        lo demás.
      </p>

      <ul className="lista-clara">
        <li>
          <strong>No se borra su cuenta.</strong> Si es tutor de otro alumno del
          club, esa relación sigue igual.
        </li>
        <li>
          <strong>No se le avisa.</strong> No le llega ningún correo: si quieres
          que lo sepa, díselo tú.
        </li>
      </ul>

      {revocar.isError && <PanelError problema={aProblema(revocar.error)} />}

      <div className="hueco-acciones">
        <button
          type="button"
          className="btn-peligro"
          disabled={revocar.isPending}
          onClick={() => revocar.mutate()}
        >
          {revocar.isPending ? 'Quitando…' : 'Sí, quitarle el acceso'}
        </button>
        <button
          type="button"
          className="btn-plano"
          onClick={alCancelar}
          disabled={revocar.isPending}
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}
